import { Box, Divider, Stack, SvgIconProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React, { useState, FC, ChangeEvent, useEffect } from 'react'
import { H1Mail, J2Shoppingcard } from '@e3dc-react/icons'
import ConditionalTooltipCheckbox from 'src/Libs/inputs/formik/ConditionalTooltipCheckbox'

const useStyles = makeStyles()(theme => ({
  itemDisabled: {
    color: theme.palette.grey.grey05,
  },
}))

interface FieldElement {
  isDisabled: boolean
  icon: FC<SvgIconProps>
  checked: boolean
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  disableReason: string
}

interface PermissionProps {
  userId: number
  hasShopAccess: boolean
  disableNotification: boolean
  disableShopAccess: boolean
  hasNotificationAccess: boolean
  disableNotificationReason: string
  disableShopReason: string
  onToggleNotificationAccess: (value: boolean, userId: number) => void
  onToggleShopAccess: (value: boolean, userId: number) => void
}

/**
 * This component can be used as part of a table. It displays access permissions and enables to change them.
 */
const CompanyPermissionsField: FC<PermissionProps> = ({
  userId,
  disableNotification,
  disableShopAccess,
  disableNotificationReason,
  disableShopReason,
  hasShopAccess,
  hasNotificationAccess,
  onToggleNotificationAccess,
  onToggleShopAccess,
}) => {
  const { classes } = useStyles()

  const [hasNotificationChecked, setHasNotificationAccess] = useState(hasNotificationAccess)
  const [hasShopChecked, setHasShopAccess] = useState(hasShopAccess)

  const handleNotificationChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = event.target.checked

    setHasNotificationAccess(isChecked)
    onToggleNotificationAccess(isChecked, userId)
  }

  const handleShopChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = event.target.checked

    setHasShopAccess(isChecked)
    onToggleShopAccess(isChecked, userId)
  }

  const fieldElements: FieldElement[] = [
    {
      isDisabled: disableNotification,
      icon: H1Mail,
      checked: hasNotificationChecked,
      onChange: handleNotificationChange,
      disableReason: disableNotificationReason,
    },
    {
      isDisabled: disableShopAccess,
      icon: J2Shoppingcard,
      checked: hasShopChecked,
      onChange: handleShopChange,
      disableReason: disableShopReason,
    },
  ]

  useEffect(() => {
    setHasNotificationAccess(hasNotificationAccess)
    setHasShopAccess(hasShopAccess)
  }, [hasNotificationAccess, hasShopAccess])

  return (
    <Stack direction="row" spacing={1}>
      {fieldElements.map(({ checked, disableReason, icon: Icon, isDisabled, onChange }, index, elements) => (
        <React.Fragment key={`employee-permissions-row-${index}`}>
          <Stack direction="row" alignItems="center">
            <Icon className={isDisabled ? classes.itemDisabled : undefined} />
            <ConditionalTooltipCheckbox checked={checked} onChange={onChange} disabled={isDisabled} disableReason={disableReason} />
          </Stack>
          {index !== elements.length - 1 && (
            <Box sx={{ flexDirection: 'row', py: 2 }}>
              <Divider orientation="vertical" />
            </Box>
          )}
        </React.Fragment>
      ))}
    </Stack>
  )
}

export default CompanyPermissionsField
